/**
 * Sprint 9 - Batch 1.1: Lead Funnel Component
 * Visual funnel showing lead flow through stages with conversion metrics
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { LEAD_STATUSES, LEAD_STATUS_GROUPS } from '../context/ClinicDataContext';
import { TrendingUp, TrendingDown, Filter, ChevronDown } from 'lucide-react';
import { isMarketplaceLead, isProviderOwnedLead } from '../utils/leadOrigin'; // TREATX-LEAD-ORIGIN-DISPLAY-01

// SALES-CRM-SIMPLIFICATION (Dashboard C1) — the dashboard funnel uses the SAME human
// sales stages as every other surface, read from the backend `human_stage` projection.
// No internal/legacy stages (Assigned/Contacted/Offer Sent/Deposit Paid/Closed Won) here.
const FUNNEL_STAGES = [
  { key: 'NEW', labelKey: 'provider.leads.stage.NEW', color: 'bg-sky-400', textColor: 'text-sky-700' },
  { key: 'WORKING', labelKey: 'provider.leads.stage.WORKING', color: 'bg-indigo-400', textColor: 'text-indigo-700' },
  { key: 'ON_HOLD', labelKey: 'provider.leads.stage.ON_HOLD', color: 'bg-slate-400', textColor: 'text-slate-700' },
  { key: 'WON', labelKey: 'provider.leads.stage.WON', color: 'bg-emerald-500', textColor: 'text-emerald-700' },
  { key: 'LOST', labelKey: 'provider.leads.stage.LOST', color: 'bg-red-400', textColor: 'text-red-700' },
];

// Time period filter options
const TIME_PERIODS = [
  { key: 'all', labelKey: 'provider.leadFunnel.allTime' },
  { key: 'month', labelKey: 'provider.leadFunnel.thisMonth' },
  { key: 'week', labelKey: 'provider.leadFunnel.thisWeek' },
];

/**
 * Filter leads by time period based on createdAt timestamp
 */
const filterByTimePeriod = (leads, period) => {
  if (period === 'all') return leads;
  
  const now = new Date();
  let cutoff;
  
  if (period === 'week') {
    cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  } else if (period === 'month') {
    cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
  }
  
  return leads.filter(lead => {
    const createdAt = new Date(lead.createdAt);
    return createdAt >= cutoff;
  });
};

/**
 * Calculate funnel metrics from leads
 */
const calculateFunnelMetrics = (leads) => {
  const stageCounts = {};
  
  // Initialize all stages with 0
  FUNNEL_STAGES.forEach(stage => {
    stageCounts[stage.key] = 0;
  });
  
  // Count leads per HUMAN stage (backend projection), never raw status.
  leads.forEach(lead => {
    const hs = lead.status_transitions?.human_stage;
    if (hs && stageCounts.hasOwnProperty(hs)) {
      stageCounts[hs]++;
    }
  });
  
  // Calculate totals
  const totalLeads = leads.length;
  const closedWon = stageCounts['WON'];
  const closedLost = stageCounts['LOST'];
  const totalClosed = closedWon + closedLost;
  const conversionRate = totalClosed > 0 ? Math.round((closedWon / totalClosed) * 100) : 0;
  
  // Calculate stage metrics
  const stageMetrics = FUNNEL_STAGES.map((stage, index) => {
    const count = stageCounts[stage.key];
    const percentage = totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0;
    
    return {
      ...stage,
      count,
      percentage,
      barWidth: totalLeads > 0 ? Math.max(5, (count / totalLeads) * 100) : 5
    };
  });
  
  return {
    totalLeads,
    closedWon,
    closedLost,
    conversionRate,
    stageMetrics,
    stageCounts
  };
};

/**
 * LeadFunnel Component
 * @param {Array} leads - Array of lead objects
 * @param {string} title - Optional title override
 * @param {boolean} showSourceBreakdown - Show marketplace vs provider-owned split
 * @param {boolean} compact - Use compact layout (for Sales Rep view)
 */
export const LeadFunnel = ({ 
  leads = [], 
  title, 
  showSourceBreakdown = true,
  compact = false 
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title || t('provider.leadFunnel.titleDefault');
  const [timePeriod, setTimePeriod] = useState('all');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Filter leads by time period
  const filteredLeads = useMemo(() => {
    return filterByTimePeriod(leads, timePeriod);
  }, [leads, timePeriod]);
  
  // Calculate metrics
  const metrics = useMemo(() => {
    return calculateFunnelMetrics(filteredLeads);
  }, [filteredLeads]);
  
  // Source breakdown (Marketplace vs Provider-Owned)
  const sourceBreakdown = useMemo(() => {
    const marketplace = filteredLeads.filter(isMarketplaceLead);
    const providerOwned = filteredLeads.filter(isProviderOwnedLead);
    
    return {
      marketplace: {
        count: marketplace.length,
        percentage: filteredLeads.length > 0 ? Math.round((marketplace.length / filteredLeads.length) * 100) : 0
      },
      providerOwned: {
        count: providerOwned.length,
        percentage: filteredLeads.length > 0 ? Math.round((providerOwned.length / filteredLeads.length) * 100) : 0
      }
    };
  }, [filteredLeads]);

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            {resolvedTitle}
          </CardTitle>
          
          {/* Time Period Filter */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              data-testid="funnel-time-filter"
            >
              <Filter className="h-4 w-4" />
              {t(TIME_PERIODS.find(p => p.key === timePeriod)?.labelKey)}
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[140px]">
                {TIME_PERIODS.map(period => (
                  <button
                    key={period.key}
                    onClick={() => {
                      setTimePeriod(period.key);
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      timePeriod === period.key ? 'bg-blue-50 text-blue-600' : ''
                    }`}
                  >
                    {t(period.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Summary Stats */}
        <div className="flex items-center gap-4 mt-2 text-sm">
          <span className="text-gray-600">
            <span className="font-semibold text-gray-900">{metrics.totalLeads}</span> {t('provider.leadFunnel.totalLeads')}
          </span>
          <span className="text-green-600 flex items-center gap-1">
            <TrendingUp className="h-4 w-4" />
            <span className="font-semibold">{metrics.conversionRate}%</span> {t('provider.leadFunnel.winRate')}
          </span>
          <span className="text-emerald-600">
            <span className="font-semibold">{metrics.closedWon}</span> {t('provider.leadFunnel.won')}
          </span>
          <span className="text-red-500">
            <span className="font-semibold">{metrics.closedLost}</span> {t('provider.leadFunnel.lost')}
          </span>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        {/* Funnel Visualization */}
        <div className="space-y-2">
          {metrics.stageMetrics.map((stage, index) => (
            <div key={stage.key} className="flex items-center gap-3">
              {/* Stage Label */}
              <div className="w-24 text-right">
                <span className={`text-sm font-medium ${stage.textColor}`}>
                  {t(stage.labelKey)}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                <div 
                  className={`h-full ${stage.color} transition-all duration-500 ease-out rounded-lg`}
                  style={{ width: `${stage.barWidth}%` }}
                />
                {/* Count overlay */}
                <div className="absolute inset-0 flex items-center px-3">
                  <span className={`text-sm font-bold ${stage.count > 0 ? 'text-white' : 'text-gray-400'}`}>
                    {stage.count}
                  </span>
                </div>
              </div>
              
              {/* Percentage */}
              <div className="w-14 text-right">
                <span className="text-sm text-gray-500">{stage.percentage}%</span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Source Breakdown */}
        {showSourceBreakdown && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium text-gray-700 mb-2">{t('provider.leadFunnel.leadSources')}</p>
            <div className="flex gap-4">
              <div className="flex-1 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">{t('provider.leadFunnel.treatxMarketplace')}</span>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                    {sourceBreakdown.marketplace.percentage}%
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {sourceBreakdown.marketplace.count}
                </p>
              </div>
              <div className="flex-1 p-3 bg-amber-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-700">{t('provider.leadFunnel.clinicOwned')}</span>
                  <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                    {sourceBreakdown.providerOwned.percentage}%
                  </Badge>
                </div>
                <p className="text-2xl font-bold text-amber-600 mt-1">
                  {sourceBreakdown.providerOwned.count}
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Simplified Pipeline View for Sales Rep
 * Groups leads into: Needs Action, In Progress, Won, Lost
 */
export const SalesRepPipeline = ({ leads = [], repName = "My" }) => {
  const { t } = useTranslation();
  const [timePeriod, setTimePeriod] = useState('all');
  const [showDropdown, setShowDropdown] = useState(false);
  
  // Filter leads by time period
  const filteredLeads = useMemo(() => {
    return filterByTimePeriod(leads, timePeriod);
  }, [leads, timePeriod]);
  
  // Calculate pipeline metrics
  const pipeline = useMemo(() => {
    // QOF-03: classifier extended to recognise the full canonical
    // backend lead lifecycle. Pre-fix the dashboard reported
    // Needs Action 0 / In Progress 0 / Won 0 even when a sales rep had
    // an ORDER_CREATED lead — because ORDER_CREATED, QUALIFIED, WON,
    // NEGOTIATION, ON_HOLD were unmapped here.
    const needsAction = filteredLeads.filter(l =>
      ['NEW', 'ASSIGNED'].includes(l.status)
    );
    const inProgress = filteredLeads.filter(l =>
      ['CONTACTED', 'QUALIFIED', 'OFFER_SENT', 'QUOTE_SENT', 'NEGOTIATION', 'ORDER_CREATED', 'ON_HOLD'].includes(l.status)
    );
    const won = filteredLeads.filter(l =>
      ['WON', 'DEPOSIT_PAID', 'CLOSED_WON', 'IN_TREATMENT', 'POST_OP'].includes(l.status)
    );
    const lost = filteredLeads.filter(l =>
      ['CLOSED_LOST', 'CLOSED_CANCELLED', 'LOST'].includes(l.status)
    );
    
    const totalClosed = won.length + lost.length;
    const winRate = totalClosed > 0 ? Math.round((won.length / totalClosed) * 100) : 0;
    
    return {
      needsAction: needsAction.length,
      inProgress: inProgress.length,
      won: won.length,
      lost: lost.length,
      total: filteredLeads.length,
      winRate
    };
  }, [filteredLeads]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{t('provider.leadFunnel.repPipeline', { name: repName })}</CardTitle>
          
          {/* Time Period Filter */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Filter className="h-4 w-4" />
              {t(TIME_PERIODS.find(p => p.key === timePeriod)?.labelKey)}
              <ChevronDown className="h-4 w-4" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-[140px]">
                {TIME_PERIODS.map(period => (
                  <button
                    key={period.key}
                    onClick={() => {
                      setTimePeriod(period.key);
                      setShowDropdown(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                      timePeriod === period.key ? 'bg-blue-50 text-blue-600' : ''
                    }`}
                  >
                    {t(period.labelKey)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Pipeline Summary */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-2xl font-bold text-amber-600">{pipeline.needsAction}</p>
            <p className="text-xs text-amber-700">{t('provider.leadFunnel.needsAction')}</p>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{pipeline.inProgress}</p>
            <p className="text-xs text-blue-700">{t('provider.leadFunnel.inProgress')}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{pipeline.won}</p>
            <p className="text-xs text-green-700">{t('provider.leadFunnel.stageWon')}</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-2xl font-bold text-red-600">{pipeline.lost}</p>
            <p className="text-xs text-red-700">{t('provider.leadFunnel.stageLost')}</p>
          </div>
        </div>
        
        {/* Win Rate Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600">{t('provider.leadFunnel.winRateShort')}</span>
            <span className="font-semibold text-green-600">{pipeline.winRate}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-500"
              style={{ width: `${pipeline.winRate}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {t('provider.leadFunnel.wonSlashClosed', { won: pipeline.won, total: pipeline.won + pipeline.lost })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadFunnel;
